import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { map } from 'rxjs/operators';
import { Calculations, selectCalculations } from '../../../calculation.state';
import { lastValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../app.state';
import { formatNumber } from '../../../../common/shared/helpers/number';
import * as d3 from 'd3';
import { MatDialog } from '@angular/material/dialog';
import { ModifyPointDialogComponent } from './modify-point-dialog/modify-point-dialog.component';
import { DrawShapeService } from './draw-shape.service';
import { NamedPointInterface, ShapeInterface } from './custom-shape.constants';
import { ZoomEventInterface } from './zoom-event.interface';
import { MeasurementsType } from '../measurements.constants';
import { FormEvent } from '../../../../common/shared/constants';
import { Point } from '@angular/cdk/drag-drop';
import { SaveShapeDialogComponent } from './save-shape-dialog/save-shape-dialog.component';
import { MatMenuTrigger } from '@angular/material/menu';
import { cloneDeep, isNil } from 'lodash-es';
import { ModifyTileDialogComponent } from './modify-tile-dialog/modify-tile-dialog.component';
import { AddPointDialogComponent } from './add-point-dialog/add-point-dialog.component';
import { ShapeDto } from '../../../../../api/models/shape-dto';
import { CustomShapesMeasurementDto } from '../../../../../api/models/custom-shapes-measurement-dto';
import { CalculationsService } from '../../../../../api/services/calculations.service';
import { ProgressSpinnerService } from '../../../../components/progress-spinner/progress-spinner.service';
import { AddAccessoryDialogComponent } from './add-accessory-dialog/add-accessory-dialog.component';
import { SideAccessory } from '../../../../../api/models/side-accessory';
import { CoreComponent } from '../../../../core.component';

@Component({
  selector: 'custom-shape',
  templateUrl: './custom-shape.component.html',
  styleUrls: ['./custom-shape.component.scss'],
})
export class CustomShapeComponent extends CoreComponent implements OnInit, AfterViewInit {
  @ViewChild('drawSvg') svg!: ElementRef<SVGElement>;
  points: number[][] = [];
  private currentLine?: d3.Selection<SVGLineElement, unknown, null, any>;
  private d3Elem!: d3.Selection<SVGElement, unknown, null, any>;
  svgWidth = 1000;
  svgHeight = 600;
  canvasWidth = 1000;
  canvasHeight = 600;
  private mousedown = false;
  private currentPoint!: { x: number; y: number };
  private draggingPoints!: number[][];
  private calculationState!: Calculations;
  zoomLevel = 0;
  zoomEvent = new EventEmitter<ZoomEventInterface>();
  shapes: ShapeInterface[] = [{ name: 'Forma 1', zoomLevel: this.zoomLevel, tiles: [], points: [] }];
  currentShapeIndex = 0;
  @Output()
  eventEmitter = new EventEmitter<FormEvent<CustomShapesMeasurementDto>>();
  @Input()
  initData: MeasurementsType | null = null;
  tiles: Point[][] = [];
  addShapeMode = true;
  fontSize = 16;
  editPoint = -1;

  // we create an object that contains coordinates
  menuTopLeftPosition = { x: 0, y: 0 };

  // reference to the MatMenuTrigger in the DOM
  @ViewChild(MatMenuTrigger, { static: true })
  matMenuTrigger!: MatMenuTrigger;
  sideAccessories: Map<number, Map<number, SideAccessory>> = new Map();

  isFullscreen = document.fullscreenElement !== null;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly dialog: MatDialog,
    private readonly store: Store<AppState>,
    readonly drawShapeService: DrawShapeService,
    private readonly calculationsService: CalculationsService,
    private readonly progressSpinnerService: ProgressSpinnerService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.garbageCollector.push(
      this.store.pipe(map((state) => selectCalculations(state))).subscribe((calculationState) => {
        if (
          !this.calculationState?.models ||
          calculationState.models?.roof?.id !== this.calculationState?.models?.roof?.id
        ) {
          this.tiles = [];
          this.shapes = this.shapes.map((shape) => {
            shape.tiles = [];
            return shape;
          });
          this.clearTiles();
          this.updateState();
        }
        this.calculationState = calculationState;
      }),
    );
    document.addEventListener('fullscreenchange', this.handleFullScreenChange.bind(this));
  }

  ngAfterViewInit(): void {
    this.d3Elem = d3.select(this.svg.nativeElement);
    this.garbageCollector.push(
      this.zoomEvent.subscribe(({ zoomLevel, zoomValue }) => {
        if (zoomLevel !== undefined) {
          this.points = this.points.map((point) => [
            this.getFullSize(point[0]) * this.scale,
            this.getFullSize(point[1]) * this.scale,
          ]);
          this.zoomLevel = zoomLevel;
        } else if (zoomValue !== undefined) {
          const newScale = 1 + (this.zoomLevel + zoomValue) * 0.1;
          this.points = this.points.map((point) => [
            this.getFullSize(point[0]) * newScale,
            this.getFullSize(point[1]) * newScale,
          ]);
          this.zoomLevel += zoomValue;
        }
        this.drawFolds();
        this.movePolygonBottomLeft();
        this.drawTiles();
      }),
    );
    this.drawFolds();
    const initData = this.initData as CustomShapesMeasurementDto;
    if (initData?.shapes?.length) {
      this.shapes = cloneDeep(initData.shapes).map((shape, index) => {
        const zoomLevel = shape.zoomLevel ?? 0;
        const scale = 1 + zoomLevel * 0.1;
        return {
          name: shape.name ?? `Forma ${index + 1}`,
          zoomLevel,
          tiles: shape.tiles ?? [],
          points: shape.points.map(({ x, y }) => [
            formatNumber(Number(x) * 100 * scale, 0),
            formatNumber(this.svgHeight - Number(y) * 100 * scale, 0),
          ]),
        };
      });
      if (initData.sideAccessories.length && initData.sideAccessories[0].sideIndex) {
        initData.sideAccessories.forEach((side) => {
          if (!this.sideAccessories.has(side.shapeIndex)) {
            this.sideAccessories.set(side.shapeIndex, new Map());
          }
          this.sideAccessories.get(side.shapeIndex)?.set(side.sideIndex, side);
        });
      }
      this.loadShape(0);
      this.updateState(true);
    }
  }

  onMouseDown(event: MouseEvent): void {
    if (!this.addShapeMode) {
      return;
    }
    // Create the polygon only if we have three or more points and we are clicking on the rectangular helping shape
    if ((event.target as HTMLElement).tagName === 'rect' && this.points.length >= 3) {
      this.createPolygon();
      this.clearHelperShapes();
      this.saveShape();
    } else {
      // Otherwise, create another helping line or rectangular
      this.addHelperShapes(event);
    }
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.addShapeMode) {
      return;
    }
    const coordinates = this.drawShapeService.getMousePosition(event);
    if ((event.target as HTMLElement).tagName === 'rect' && this.points.length >= 3) {
      this.renderer.setAttribute(event.target, 'stroke', '#0784fa');
    } else {
      const rect: SVGRectElement | null = this.svg.nativeElement.querySelector('rect');
      if (rect) {
        this.renderer.setAttribute(rect, 'stroke', 'transparent');
      }
    }
    // Move line by changing the coordinates
    if (this.currentLine) {
      this.currentLine.attr('x2', coordinates.x).attr('y2', coordinates.y);
      this.clearTexts();
      this.d3Elem
        .append('text')
        .attr('class', 'temp-text')
        .attr('x', coordinates.x)
        .attr('y', coordinates.y - 20)
        .attr('fill', 'black')
        .text(
          `${formatNumber(this.getFullSize(coordinates.x / 100))}; ${formatNumber(
            this.getFullSize(this.svgHeight - coordinates.y) / 100,
          )}`,
        );
    }
  }

  get scale() {
    return 1 + this.zoomLevel * 0.1;
  }

  getFullSize(number: number, zoomLevel?: number): number {
    let scale = this.scale;
    if (zoomLevel !== undefined) {
      scale = 1 + zoomLevel * 0.1;
    }
    return number / scale;
  }

  private drawFolds() {
    const elements = this.svg.nativeElement.querySelectorAll('.squares');
    elements.forEach((element) => {
      element.remove();
    });
    const maxFolds = Math.ceil(this.svgHeight / (35 * this.scale));
    for (let i = 0; i <= maxFolds; i++) {
      this.d3Elem
        .append('line')
        .attr('x1', 0)
        .attr('x2', this.svgWidth)
        .attr('y1', this.svgHeight - i * (35 * this.scale))
        .attr('y2', this.svgHeight - i * (35 * this.scale))
        .attr('style', 'stroke:rgb(0,0,0, 0.3);stroke-width:1')
        .attr('class', 'squares');
    }
  }

  /**
   * After each mouse click, we add a point into the array of polygon coordinates
   * and insert the next helping line.
   * @param event Mouse Event
   */
  private addHelperShapes(event: MouseEvent) {
    const coordinates = this.drawShapeService.getMousePosition(event);
    this.points.push([coordinates.x, coordinates.y]);
    if (this.points.length === 1) {
      this.d3Elem
        .append('rect')
        .attr('x', coordinates.x - 10)
        .attr('y', coordinates.y - 10)
        .attr('width', '20')
        .attr('height', '20')
        .attr('fill', 'transparent')
        .attr('class', 'help');
    }
    this.currentLine = this.d3Elem
      .insert('line', ':nth-child(1)')
      .attr('x1', coordinates.x)
      .attr('x2', coordinates.x)
      .attr('y1', coordinates.y)
      .attr('y2', coordinates.y)
      .attr('stroke', '#0784fa')
      .attr('class', 'help');
  }

  private createPolygon() {
    if (!this.points.length) {
      return;
    }
    this.d3Elem
      .append('polygon')
      .attr('id', 'polygon')
      .attr('points', this.points.join(' '))
      .attr('fill', 'lightgrey')
      .attr('stroke', 'black')
      .attr('stroke-width', '0.2')
      .on('mousemove', (event) => {
        this.polygonMouseMove(event);
      })
      .on('mouseup', () => {
        this.polygonMouseUp();
      })
      .on('mousedown', (event) => {
        this.polygonMouseDown(event);
      });
    let index = 0;
    const namedPoints = this.drawShapeService.getNamedPoints(this.points);
    for (const point of namedPoints) {
      // const nextPoint = namedPoints[index + 1] ?? namedPoints[0];
      // const x = point.x - nextPoint.x;
      // const y = point.y - nextPoint.y;
      // const lineText = this.getLineText(namedPoints, index);
      // this.d3Elem
      //   .append('text')
      //   .attr('class', 'line-text')
      //   .attr('x', point.x - x / 2)
      //   .attr('y', point.y - y / 2)
      //   .attr('font-size', this.fontSize)
      //   .text(lineText);
      this.d3Elem
        .append('text')
        .attr('class', 'polygon-text')
        .attr('x', point.x)
        .attr('y', point.y - 10)
        .attr('data-point-index', index)
        .attr('font-size', 24)
        .on('click', (event) => this.pointAdjustClickEvent(event));
      this.d3Elem
        .append('circle')
        .attr('cx', point.x)
        .attr('cy', point.y)
        .attr('r', '8')
        .attr('fill', 'blue')
        .attr('class', 'polygon-point')
        .attr('data-point-index', index)
        .on('click', (event) => this.pointAdjustClickEvent(event));
      index++;
    }
    if (this.points.length > 2) {
      this.addShapeMode = false;
      this.clearTexts();
      this.movePolygonBottomLeft();
    }
  }

  /**
   * Clear the helping lines and rectangular
   */
  private clearHelperShapes() {
    this.svg.nativeElement.querySelectorAll('.help').forEach((element) => {
      element.remove();
    });
  }

  /**
   * Clear the helping lines and rectangular
   */
  private clearTexts() {
    const texts = this.svg.nativeElement.querySelectorAll('.temp-text');
    texts.forEach((element) => {
      element.remove();
    });
  }

  private polygonMouseDown(event: MouseEvent) {
    this.mousedown = true;
    const coordinates = this.drawShapeService.getMousePosition(event);
    const target = event.target as SVGPolygonElement;
    if (target.tagName === 'polygon') {
      this.currentPoint = coordinates;
      this.draggingPoints = this.points;
    }
  }

  private polygonMouseUp() {
    this.mousedown = false;
  }

  private polygonMouseMove(event: MouseEvent) {
    if (this.tiles.length) {
      return;
    }
    const coordinates = this.drawShapeService.getMousePosition(event);
    if (this.mousedown) {
      const x = coordinates.x - this.currentPoint.x;
      const y = coordinates.y - this.currentPoint.y;
      const h = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
      const angle = this.drawShapeService.angleFromCoordinates(x, y);
      this.points = this.drawShapeService.polygonTranslate(this.draggingPoints, angle, h);
      this.movePolygon();
    }
  }

  pointAdjustClickEvent(event: Event, index?: number) {
    const pointIndex = index ?? Number((event.target as Element).getAttribute('data-point-index'));
    this.editPoint = pointIndex;
    this.dialog
      .open(ModifyPointDialogComponent, {
        width: '350px',
        minHeight: '370px',
        height: '370px',
        data: {
          x: formatNumber(this.getFullSize(this.points[pointIndex][0]) / 100),
          y: formatNumber(this.getFullSize(this.svgHeight - this.points[pointIndex][1]) / 100),
          point: this.drawShapeService.getPointName(pointIndex),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        this.editPoint = -1;
        if (!result) {
          return;
        }
        this.points[pointIndex] = [
          Number(result.x) * 100 * this.scale,
          this.svgHeight - Number(result.y) * 100 * this.scale,
        ];
        this.movePolygon();
        this.saveShape();
        this.clearTilesButtonClick();
      });
  }

  openAddPointDialog() {
    this.dialog
      .open(AddPointDialogComponent, {
        width: '350px',
        minHeight: this.points.length > 1 ? '500px' : '350px',
        height: this.points.length > 1 ? '500px' : '350px',
        data: {
          namedPoints: this.drawShapeService.getNamedPoints(this.points),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        if (!isNil(result.afterPointIndex)) {
          this.points.splice(result.afterPointIndex, 0, [
            result.x * 100 * this.scale,
            this.svgHeight - result.y * 100 * this.scale,
          ]);
        }
        const points = [...this.points];
        this.clearShape();
        this.points = points;
        this.createPolygon();
        if (this.points.length > 2) {
          this.saveShape();
        }
      });
  }

  addAccessoryDialog(sideIndex: number) {
    const side = this.drawShapeService.getSides(this.points)[sideIndex];
    this.dialog
      .open(AddAccessoryDialogComponent, {
        width: '400px',
        minHeight: '450px',
        height: '450px',
        data: {
          side,
          sideAccessory: this.sideAccessories.get(this.currentShapeIndex)?.get(sideIndex),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        if (!result.accessories.length) {
          if (this.sideAccessories.has(this.currentShapeIndex)) {
            this.sideAccessories.get(this.currentShapeIndex)?.delete(sideIndex);
          }
        } else {
          // console.log(
          //   this.sideAccessories,
          //   this.currentShapeIndex,
          //   sideIndex,
          //   this.sideAccessories.get([this.currentShapeIndex],
          //   this.sideAccessories[this.currentShapeIndex][sideIndex],
          // );
          if (!this.sideAccessories.has(this.currentShapeIndex)) {
            this.sideAccessories.set(this.currentShapeIndex, new Map());
          }
          this.sideAccessories.get(this.currentShapeIndex)?.set(sideIndex, {
            length: this.getFullSize(side.length) / 100,
            types: result.accessories,
            sideIndex,
            shapeIndex: this.currentShapeIndex,
          });
        }
        this.updateState();
      });
  }

  private movePolygon() {
    const polygon = this.svg.nativeElement.querySelector('#polygon');
    polygon?.setAttribute('points', this.points.map((point) => [point[0], point[1]]).join(' '));
    this.svg.nativeElement.querySelectorAll('.polygon-text');
    const texts = this.svg.nativeElement.querySelectorAll('.polygon-text') as any;
    const namedPoints = this.drawShapeService.getNamedPoints(this.points);
    for (let i = 0; i < texts.length; i++) {
      const point = namedPoints[i];
      texts[i].setAttribute('x', point.x);
      texts[i].setAttribute('y', point.y - 10);
      texts[i].textContent = point.name;
    }
    const circles = this.svg.nativeElement.querySelectorAll('.polygon-point') as any;
    for (let i = 0; i < circles.length; i++) {
      circles[i].setAttribute('cx', this.points[i][0]);
      circles[i].setAttribute('cy', this.points[i][1]);
    }
  }

  async calculateTiles() {
    this.progressSpinnerService.openProgressSpinner();
    this.movePolygonBottomLeft();
    const points = this.points.map((points) => {
      return {
        x: formatNumber(this.getFullSize(points[0]) / 100),
        y: formatNumber(this.getFullSize(this.svgHeight - points[1]) / 100),
      };
    });
    this.tiles = (
      await lastValueFrom(
        this.calculationsService.calculationsControllerCustomShapeTiles({
          body: {
            roofId: this.calculationState.models?.roof.id as string,
            points,
          },
        }),
      )
    ).tiles;
    this.drawTiles();
    this.updateState();
    this.progressSpinnerService.closeProgressSpinner();
  }

  private drawTiles(activeTiles: number[] = []) {
    this.shapes[this.currentShapeIndex].tiles = this.tiles;
    this.clearTiles();
    let index = 0;
    for (const points of this.tiles || []) {
      this.d3Elem
        .append('rect')
        .attr('x', points[0].x * 100 * this.scale)
        .attr('y', this.svgHeight - points[2].y * 100 * this.scale)
        .attr('width', (points[1].x - points[0].x) * 100 * this.scale - 1)
        .attr('height', (points[2].y - points[0].y) * 100 * this.scale - 1)
        .attr('fill', 'transparent')
        .attr('class', 'tile-square' + (activeTiles.includes(index) ? ' active' : ''))
        .attr('data-tile-index', index)
        .on('click', (event: MouseEvent) => {
          this.matMenuTrigger.closeMenu();
          this.changeActiveTile(event.target as Element, event.ctrlKey, event.button === 0);
        })
        .on('contextmenu', (event: MouseEvent) => this.onTileRightClick(event));

      this.d3Elem
        .append('text')
        .text(`${formatNumber(points[2].y - points[0].y)}m`)
        .attr('x', (points[0].x * 100 + 20) * this.scale)
        .attr('y', this.svgHeight - (points[2].y * 100 - 30) * this.scale)
        .attr('fill', 'black')
        .attr('class', 'tile-square-text')
        .attr('font-size', this.fontSize)
        .attr('data-tile-index', index)
        .on('click', (event: MouseEvent) => {
          this.matMenuTrigger.closeMenu();
          const element = event.target as Element;
          this.changeActiveTile(element.previousElementSibling as Element, event.ctrlKey, event.button === 0);
        })
        .on('contextmenu', (event: MouseEvent) => {
          const element = event.target as Element;
          this.onTileRightClick(event, element.previousElementSibling as Element);
        });
      index++;
    }
  }

  clearShapeButtonClick() {
    let name = 'Forma';
    if (this.currentShapeIndex !== undefined) {
      name = this.shapes[this.currentShapeIndex].name;
    }
    if (!confirm('Ești sigur că vrei să ștergi ' + name + '?')) {
      return;
    }
    if (this.currentShapeIndex !== undefined) {
      this.shapes.splice(this.currentShapeIndex, 1);
      this.sideAccessories.delete(this.currentShapeIndex);
    }
    this.clearShape();
    if (this.shapes.length === 0) {
      this.shapes.push({ name: 'Forma 1', zoomLevel: this.zoomLevel, points: [], tiles: [] });
    }
    this.currentShapeIndex = this.shapes.length - 1;
    this.updateState();
    this.loadShape(this.currentShapeIndex);
  }

  clearShape() {
    this.points = [];
    this.addShapeMode = true;
    this.svg.nativeElement.innerHTML = '';
    this.drawFolds();
    this.tiles = [];
  }

  saveShapeButtonClick() {
    this.dialog
      .open(SaveShapeDialogComponent, {
        width: '350px',
        minHeight: '300px',
        height: '300px',
        data: {
          name: this.shapes[this.currentShapeIndex]?.name || 'Forma 1',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        const originalShapeIndex = this.currentShapeIndex;
        for (let i = 0; i < result.copyCount; i++) {
          // The first shape is the original, that's why we're skipping creation
          if (i > 0) {
            this.shapes.push({
              ...cloneDeep(this.shapes[originalShapeIndex]),
              name: `${result.name} #${i + 1}`,
            });
            this.currentShapeIndex = this.shapes.length - 1;
          } else {
            this.shapes[this.currentShapeIndex].name = result.name;
          }
        }
        this.updateState();
      });
  }

  saveShape(name = this.shapes[this.currentShapeIndex]?.name) {
    this.shapes[this.currentShapeIndex] = {
      name,
      points: this.points,
      zoomLevel: this.zoomLevel,
      tiles: this.tiles,
    };
    this.updateState();
  }

  private updateState(unchanged = false) {
    const event: FormEvent<CustomShapesMeasurementDto> = {
      value: {
        shapes: this.getApiShapes(),
        sideAccessories: Array.from(this.sideAccessories, ([key, value]) =>
          Array.from(value, ([key, side]) => side),
        ).reduce((shape, array) => {
          return [...array, ...shape];
        }, []),
      },
      valid: this.shapes.filter((shape) => shape.points.length > 0).length > 0,
      unchanged,
    };
    this.eventEmitter.emit(event);
  }

  getApiShapes(): ShapeDto[] {
    return cloneDeep(this.shapes)
      .filter((shape) => shape.points.length > 2)
      .map(({ points, zoomLevel, tiles, name }) => {
        return {
          points: points.map((point) => ({
            x: formatNumber(this.getFullSize(point[0] / 100, zoomLevel)),
            y: formatNumber(this.getFullSize((this.svgHeight - point[1]) / 100, zoomLevel)),
          })),
          tiles,
          zoomLevel,
          name,
        };
      });
  }

  newShape() {
    this.clearShape();
    this.shapes.push({ name: 'Forma ' + (this.shapes.length + 1), points: [], zoomLevel: this.zoomLevel, tiles: [] });
    this.updateState();
    this.currentShapeIndex = this.shapes.length - 1;
  }

  loadShape(index: number): void {
    if (this.points.length && !this.addShapeMode) {
      this.saveShape();
    }
    this.clearShape();
    const shape = this.shapes[index];
    this.currentShapeIndex = index;
    this.points = shape.points;
    this.tiles = shape.tiles ?? [];
    this.createPolygon();
    this.drawTiles();
    if (this.zoomLevel !== shape.zoomLevel) {
      this.zoomEvent.emit({ zoomLevel: shape.zoomLevel });
    }
  }

  private movePolygonBottomLeft() {
    if (!this.points.length) {
      return;
    }
    this.points = this.drawShapeService.movePointsForCalculation(this.points, this.svgHeight);
    this.movePolygon();
  }

  private clearTiles() {
    const elements = this.svg?.nativeElement?.querySelectorAll('.tile-square, .tile-square-text') || [];
    elements.forEach((element) => {
      element.remove();
    });
  }

  clearTilesButtonClick() {
    this.tiles = [];
    this.shapes[this.currentShapeIndex].tiles = [];
    this.clearTiles();
    this.updateState();
  }

  onTileRightClick(event: MouseEvent, targetElement = event.target as Element) {
    if (targetElement.classList.contains('active')) {
      this.changeActiveTile(targetElement, true, false);
    } else {
      this.matMenuTrigger.closeMenu();
      this.changeActiveTile(targetElement, false, false);
    }

    // we record the mouse position in our object
    this.menuTopLeftPosition.x = event.clientX;
    this.menuTopLeftPosition.y = event.clientY;

    // we open the menu
    // we pass to the menu the information about our object
    this.matMenuTrigger.menuData = { items: this.elementRef.nativeElement.querySelectorAll('.tile-square.active') };

    // we open the menu
    this.matMenuTrigger.openMenu();
  }

  changeActiveTile(tileElement: Element, multiselect = false, leftClick = true) {
    if (!multiselect) {
      for (const prevActive of this.elementRef.nativeElement.querySelectorAll('.tile-square.active')) {
        prevActive.classList.remove('active');
      }
    }
    if (leftClick) {
      tileElement.classList.toggle('active');
    } else {
      tileElement.classList.add('active');
    }
  }

  deleteTile(tileElements: Element[]) {
    const tileIndexes = Array.from(tileElements)
      .map((tileElement) => Number(tileElement.getAttribute('data-tile-index')))
      .sort((a, b) => a - b);
    let deletedCount = 0;
    for (const tileIndex of tileIndexes) {
      this.tiles.splice(tileIndex - deletedCount, 1);
      deletedCount++;
    }
    this.shapes[this.currentShapeIndex].tiles = this.tiles;
    this.drawTiles();
    this.updateState();
  }

  modifyTilesPreset(tileElements: Element[], length: number, start: 'up' | 'down') {
    const tileIndexes = [];
    for (const tileElement of tileElements) {
      const tileIndex = Number(tileElement.getAttribute('data-tile-index'));
      tileIndexes.push(tileIndex);
      if (start === 'up') {
        this.tiles[tileIndex][2].y += length;
        this.tiles[tileIndex][3].y += length;
      } else {
        this.tiles[tileIndex][0].y += length;
        this.tiles[tileIndex][1].y += length;
        this.tiles[tileIndex][4].y += length;
      }
    }
    this.shapes[this.currentShapeIndex].tiles = this.tiles;
    this.drawTiles(tileIndexes);
    this.updateState();
  }

  splitTiles(tileElements: Element[]) {
    let addedCount = 0;
    const tileIndexes = Array.from(tileElements)
      .map((tileElement) => Number(tileElement.getAttribute('data-tile-index')))
      .sort((a, b) => a - b);
    for (const tileIndex of tileIndexes) {
      const actualIndex = tileIndex + addedCount;
      const originalHeight = this.tiles[actualIndex][2].y - this.tiles[actualIndex][0].y;

      const originalTotalHeight = this.tiles[actualIndex][2].y;
      const height = this.drawShapeService.getRoundedTileHeight(originalHeight);
      const newHeight = this.drawShapeService.getRoundedTileHeight(height / 2);
      if (newHeight < 0.5 || originalHeight - newHeight < 0.5) {
        continue;
      }
      this.tiles[actualIndex][2].y = this.tiles[actualIndex][0].y + newHeight;
      this.tiles[actualIndex][3].y = this.tiles[actualIndex][0].y + newHeight;
      const newTile = cloneDeep(this.tiles[actualIndex]);
      newTile[0].y = this.tiles[actualIndex][2].y - 0.15;
      newTile[1].y = this.tiles[actualIndex][3].y - 0.15;
      newTile[2].y = originalTotalHeight;
      newTile[3].y = originalTotalHeight;
      newTile[4].y = newHeight - 0.15;
      this.tiles.splice(actualIndex, 0, newTile);
      addedCount++;
    }
    this.shapes[this.currentShapeIndex].tiles = this.tiles;
    this.drawTiles();
    this.updateState();
  }

  modifyTiles(tileElements: Element[]) {
    const tileIndexes = Array.from(tileElements)
      .map((tileElement) => Number(tileElement.getAttribute('data-tile-index')))
      .sort((a, b) => a - b);
    this.dialog
      .open(ModifyTileDialogComponent, {
        width: '350px',
        minHeight: '300px',
        height: '300px',
        data: {
          y: formatNumber(this.tiles[tileIndexes[0]][2].y - this.tiles[tileIndexes[0]][0].y),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result) {
          return;
        }
        for (const tileIndex of tileIndexes) {
          this.tiles[tileIndex][2].y = formatNumber(this.tiles[tileIndex][0].y + Number(result.y));
          this.tiles[tileIndex][3].y = formatNumber(this.tiles[tileIndex][1].y + Number(result.y));
        }
        this.shapes[this.currentShapeIndex].tiles = this.tiles;
        this.drawTiles(tileIndexes);
        this.updateState();
      });
  }

  getLineText(namedPoints: NamedPointInterface[], index: number): string {
    const nextPoint = namedPoints[index + 1] ?? namedPoints[0];
    const point = namedPoints[index];
    const distance = this.getFullSize(
      this.drawShapeService.getDistanceBetweenPoints(point.x, point.y, nextPoint.x, nextPoint.y),
    );
    return `${point.name}${nextPoint.name} ${formatNumber(distance / 100)}m`;
  }

  formatPoint(number: number): string {
    return number.toFixed(2);
  }

  deletePoint(i: number) {
    if (confirm('Esti sigur ca vrei sa stergi punctul ' + this.drawShapeService.getPointName(i) + '?')) {
      const points = [...this.points];
      this.clearShape();
      points.splice(i, 1);
      this.points = points;
      this.createPolygon();
      if (this.points.length > 2) {
        this.saveShape();
      }
    }
  }

  toggleFullScreen() {
    const elem = this.elementRef.nativeElement.querySelector('#shape-editor');
    if (document.fullscreenElement) {
      document.exitFullscreen();
      this.isFullscreen = false;
    } else {
      elem.requestFullscreen();
      this.isFullscreen = true;
    }
  }

  handleFullScreenChange() {
    this.isFullscreen = document.fullscreenElement !== null;
    if (this.isFullscreen) {
      this.setFullScreenShapeEditorSize();
    } else {
      this.canvasWidth = this.svgWidth = 1000;
      this.canvasHeight = this.svgHeight = 600;
      this.loadShape(this.currentShapeIndex);
    }
  }

  setFullScreenShapeEditorSize() {
    setTimeout(() => {
      const elem = this.elementRef.nativeElement.querySelector('#shape-editor');
      this.canvasWidth = this.svgWidth = elem.clientWidth - 280;
      this.canvasHeight = this.svgHeight = elem.clientHeight - 160;
      this.loadShape(this.currentShapeIndex);
    }, 100);
  }
}
